import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

// Fáze 4: statistiky týmu. Sloučí per-hra skóre ze 3 zdrojů (viz StatsData). Typ = přepínač
// (vždy 1 typ, nemíchají se). Grafy: vývoj v čase (session = 1 bod), porovnání hráčů, dle zdroje.

@Composable
fun StatsSection(token: String, profiles: List<SgProfileDto>) {
    val nameById = remember(profiles) { profiles.associate { it.blPlayerId to it.displayName } }
    var games by remember { mutableStateOf<List<StatGame>>(emptyList()) }
    var seasons by remember { mutableStateOf<List<Pair<String, String>>>(emptyList()) }
    var sheets by remember { mutableStateOf<List<PlayerSheetDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    var enabledSources by remember { mutableStateOf(setOf("league")) }
    var selectedType by remember { mutableStateOf("classic") }
    var selectedSeason by remember { mutableStateOf<String?>(null) }
    var selectedEvent by remember { mutableStateOf<String?>(null) }
    var statsTab by remember { mutableStateOf("overview") }
    LaunchedEffect(Unit) {
        try {
            val (g, s) = loadStatGames(token)
            games = g; seasons = s
            sheets = Api.fetchTeamSheets(token)
            selectedSeason = s.firstOrNull()?.first
        } catch (e: Throwable) {
            error = e.apiMessage()
        } finally {
            loading = false
        }
    }

    val seasonYearById = remember(games) { seasonYears(games) }
    val seasonFiltered = games.filter { g ->
        g.source in enabledSources && g.gameType == selectedType && g.matchesSeason(selectedSeason, seasonYearById)
    }
    val activeEvent = selectedEvent?.takeIf { id -> seasonFiltered.any { it.eventKey() == id } }
    val filtered = filterStatEvent(seasonFiltered, activeEvent)
    val sheetByKey = sheets.associateBy { "${it.blPlayerId}:${it.key()}" }
    val detailed = seasonFiltered.mapNotNull { game ->
        sheetByKey["${game.playerId}:${game.sheetKey()}"]?.let { DetailedGame(game, it) }
    }

    Div(attrs = { classes("card") }) {
        H3 { Text("Statistiky týmu") }
        when {
            loading -> P { Text("Načítám…") }
            error != null -> P { Text("Chyba: $error") }
            else -> {
                StatFilters(enabledSources, { enabledSources = enabledSources.toggle(it) }, selectedType, { selectedType = it }, seasons, selectedSeason, { selectedSeason = it }) {
                    StatEventFilter(seasonFiltered, nameById, activeEvent) { selectedEvent = it }
                }

                if (filtered.isEmpty()) {
                    P(attrs = { classes("muted") }) { Text("Žádné hry pro zvolený filtr (${bowlingTypeLabel(selectedType)}).") }
                    return@Div
                }
                StatsTabs(statsTab, listOf(
                    "overview" to "Přehled", "frames" to "Framy", "form" to "Forma",
                    "players" to "Hráči", "sources" to "Zdroje",
                )) { statsTab = it }

                when (statsTab) {
                    "frames" -> {
                        val allDetails = games.mapNotNull { game ->
                            sheetByKey["${game.playerId}:${game.sheetKey()}"]?.takeIf { it.gameType == game.gameType }?.let { DetailedGame(game, it) }
                        }
                        FrameStatsView(seasonFiltered, allDetails, nameById, selectedEvent = activeEvent)
                    }
                    "form" -> FormStatsPanel(filtered, "nejlepší série hráče")
                    "players" -> TeamPlayersStatsPanel(seasonFiltered, detailed, nameById, activeEvent)
                    "sources" -> {
                        H4 { Text("Porovnání zdrojů") }
                        BarChart(SOURCE_LABELS.mapNotNull { (code, label) ->
                            val gs = filtered.filter { it.source == code }
                            if (gs.isEmpty()) null else Triple(label, round1(gs.map { it.score }.average()), gs.size)
                        })
                    }
                    else -> {
                        Div(attrs = { classes("career-stats") }) {
                            StatPill2("${round1(filtered.map { it.score }.average())}", "průměr týmu")
                            StatPill2("${filtered.size}", "her")
                            StatPill2("${filtered.maxOf { it.score }}", "nejlepší hra")
                        }
                        H4 { Text("Vývoj výkonnosti v čase") }
                        TimeChart(timePoints(filtered), average = filtered.map { it.score }.average())
                    }
                }
            }
        }
    }
}

/** Sdílený filtrovací pruh (zdroj chips, typ přepínač, sezóna). */
@Composable
fun StatFilters(
    enabledSources: Set<String>, onToggleSource: (String) -> Unit,
    selectedType: String, onSelectType: (String) -> Unit,
    seasons: List<Pair<String, String>>, selectedSeason: String?, onSelectSeason: (String?) -> Unit,
    eventFilter: @Composable () -> Unit = {},
) {
    Div(attrs = { classes("stat-filters") }) {
        Div(attrs = { classes("filter-group") }) {
            Span(attrs = { classes("filter-label") }) { Text("Zdroj") }
            SOURCE_LABELS.forEach { (code, label) -> Chip(label, code in enabledSources) { onToggleSource(code) } }
        }
        Div(attrs = { classes("filter-group") }) {
            Span(attrs = { classes("filter-label") }) { Text("Typ") }
            BOWLING_TYPES.forEach { (code, label) -> Chip(label, code == selectedType) { onSelectType(code) } }
        }
        if (seasons.isNotEmpty()) {
            Div(attrs = { classes("filter-group") }) {
                Span(attrs = { classes("filter-label") }) { Text("Sezóna") }
                Select(attrs = { classes("field"); onChange { ev -> onSelectSeason(ev.value?.ifBlank { null }) } }) {
                    Option("", attrs = { if (selectedSeason == null) attr("selected", "selected") }) { Text("Všechny sezóny") }
                    seasons.forEach { (id, name) ->
                        Option(id, attrs = { if (selectedSeason == id) attr("selected", "selected") }) { Text(name) }
                    }
                }
            }
        }
        eventFilter()
    }
}
